<template>
  <div class="flex h-screen bg-gray-50">
    <aside class="w-64 bg-white border-r border-gray-200 flex flex-col">
      <div class="p-4 border-b border-gray-200">
        <img src="/img/logo.png" alt="manufix logo" style="float: left; width: 40px; margin-right: 3px;" />
        <h1 class="text-xl font-bold text-gray-900" style="margin-top: 7px;">manufix</h1>
      </div>

      <div class="p-4 border-b border-gray-200">
        <div class="flex items-center justify-between mb-3">
          <span class="text-sm font-semibold text-gray-700">{{ company?.name || 'Loading...' }}</span>
          <UButton
            icon="i-lucide-settings"
            color="gray"
            variant="ghost"
            size="xs"
            @click="appStore.toggleCompanySettings()"
          />
        </div>
        <UButton 
          label="Neues Projekt" 
          icon="i-lucide-plus"
          color="primary"
          block
          @click="createNewProject"
        />
      </div>

      <div class="flex-1 px-4">
        <div class="space-y-2">
          <div v-for="project in projects" :key="project.id" class="space-y-1">
            <button 
              @click="toggleProject(project.id)"
              class="flex items-center gap-2 w-full py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded"
              :class="selectedProject?.id === project.id ? 'bg-yellow-50 text-yellow-700' : ''"
            >
              <UIcon 
                :name="expandedProjects.includes(project.id) ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'" 
                class="size-4" 
              />
              <span @click.stop="selectProject(project.id)" class="flex-1 text-left">{{ project.name }}</span>
            </button>
            
            <div v-if="expandedProjects.includes(project.id)" class="ml-6 space-y-1">
              <button 
                v-for="file in [...project.uploadedFiles, ...project.generatedFiles].slice(0, 3)" 
                :key="file.id"
                class="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded"
              >
                <UIcon name="i-lucide-file-text" class="size-4" />
                <span>{{ file.name }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="p-4 bg-yellow-50 border-t border-gray-200">
        <NuxtLink to="/profile">
          <UButton 
            label="Profil verwalten" 
            icon="i-lucide-user"
            color="primary"
            variant="soft"
            block
          />
        </NuxtLink>
      </div>
    </aside>

    <main class="flex-1 overflow-hidden relative">
      <CompanySettings v-if="showCompanySettings" @close="appStore.toggleCompanySettings()" />
      <Welcome v-else-if="!selectedProject" />
      <ProjectView v-else :project="selectedProject" @update:project="updateProject" @open-chat-with-focus="openChatWithFocus" />
      
      <!-- Chat Toggle Button (shown when chat is hidden and project is selected) -->
      <UButton
        v-if="!showChat && selectedProject"
        icon="i-lucide-message-circle"
        color="primary"
        size="lg"
        class="absolute bottom-6 right-6 shadow-lg"
        @click="appStore.toggleChat()"
      />
    </main>

    <!-- Chat Sidebar (only shown when project is selected) -->
    <Chat 
      v-if="showChat && selectedProject" 
      @close="appStore.toggleChat()" 
      @upload-file="handleChatFileUpload" 
      :focus-input="appStore.chatFocusInput"
      @input-focused="appStore.closeChatFocus()"
    />

    <!-- Signup Dialog for anonymous users -->
    <SignupDialog
      :model-value="auth.showUpgradeDialog.value"
      @update:model-value="(value) => auth.showUpgradeDialog.value = value"
      @dismiss="auth.dismissUpgradeDialog"
      @signup="handleSignup"
    />
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from '~/stores/app'
import { useProjectsStore } from '~/stores/projects'
import { useCompanyStore } from '~/stores/company'
import { useAuthStore } from '~/stores/auth'
import { FileModel, FileOrigin, Project } from '~/types/models'

const auth = useAuth()

// Initialize authentication
onMounted(() => {
  auth.initializeAuth()
})

const appStore = useAppStore()
const projectsStore = useProjectsStore()
const companyStore = useCompanyStore()
const authStore = useAuthStore()

// UI State from store
const { showChat, showCompanySettings, expandedProjects } = storeToRefs(appStore)
const { projects, selectedProject } = storeToRefs(projectsStore)
const { company } = storeToRefs(companyStore)

const toggleProject = (projectId: string) => {
  appStore.toggleProject(projectId)
}

const selectProject = (projectId: string) => {
  projectsStore.selectProject(projectId)
}

const updateProject = (updates: any) => {
  if (selectedProject.value) {
    projectsStore.updateProject(selectedProject.value.id, updates)
  }
}

const openChatWithFocus = () => {
  appStore.openChatWithFocus()
}

const createNewProject = () => {
  const currentUser = authStore.currentUser
  if (!currentUser) return
  
  const projectCount = projects.value.length + 1
  const newProject = new Project({
    id: `project-${Date.now()}`,
    name: `Neues Projekt ${projectCount}`,
    instructions: '',
    createdAt: new Date(),
    createdBy: currentUser.id
  })
  
  projectsStore.addProject(newProject)
  projectsStore.selectProject(newProject.id)
  appStore.toggleProject(newProject.id) // Expand the new project
}

// Handle file upload from chat
const handleChatFileUpload = (files: File[]) => {
  if (selectedProject.value) {
    files.forEach(file => {
      const fileModel = new FileModel({
        id: `file-${Date.now()}-${Math.random()}`,
        name: file.name,
        abstract: `Uploaded file: ${file.name}`,
        origin: FileOrigin.USER_UPLOAD,
        size: file.size,
        type: file.name.split('.').pop() || '',
        uploadedAt: new Date(),
        uploadedBy: 'user-1' // Current user ID
      })
      projectsStore.addFileToProject(selectedProject.value.id, fileModel)
    })
  }
}

// Handle signup from dialog
const handleSignup = async (data: { email: string, password: string, displayName?: string }) => {
  try {
    await auth.signUpWithEmail(data.email, data.password, data.displayName)
  } catch (error) {
    console.error('Signup error:', error)
  }
}
</script>